/**
 * Shared tokenized-template parsing and preview helpers.
 *
 * 1. Parse the template against the caller's token vocabulary.
 * 2. Substitute caller-provided sample values for the live preview.
 * 3. Tidy separators and use the caller's fallback when all tokens are empty.
 *
 * The settings editor is in TokenizedTitleTemplateSetting.tsx.
 */
/** Tokens supported by coding-link title preferences. */
export const CODING_LINK_TITLE_TOKENS = [
  'CANDIDATE_NAME',
  'CANDIDATE_EMAIL_ADDRESS',
  'JOB_NAME',
  'JOB_STAGE_NAME',
  'ATS_APPLICATION_ID',
] as const;
/** Union of coding-link token names. */
export type CodingLinkTitleToken = (typeof CODING_LINK_TITLE_TOKENS)[number];
/** Sample values used by coding-link previews. */
export const CODING_LINK_PREVIEW_SAMPLE_VALUES: Record<CodingLinkTitleToken, string> = {
  CANDIDATE_NAME: 'Jordan Smith',
  CANDIDATE_EMAIL_ADDRESS: 'jordan@example.com',
  JOB_NAME: 'Staff Engineer',
  JOB_STAGE_NAME: 'Technical Screen',
  ATS_APPLICATION_ID: 'app-12345',
};
/** Zoom tokens mirror backend ZoomMeetingTopicTemplate.SUPPORTED_TOKENS. */
export const ZOOM_MEETING_TOPIC_TOKENS = [...CODING_LINK_TITLE_TOKENS, 'INTERVIEW_NAME'] as const;
/** Zoom preview samples mirror backend ZoomMeetingTopicTemplate.SUPPORTED_TOKENS. */
export const ZOOM_MEETING_TOPIC_PREVIEW_SAMPLE_VALUES = {
  ...CODING_LINK_PREVIEW_SAMPLE_VALUES,
  INTERVIEW_NAME: 'System Design Interview',
};
const TOKEN_PATTERN = /\{\{([A-Z0-9_]+)}}/g;
const SEPARATORS = '\\-–—|:•';
const EMPTY_BRACKETS = /\(\s*\)|\[\s*]|<\s*>|\{\s*}/g;
const DOUBLED_SEPARATORS = new RegExp(`([${SEPARATORS}])(?:\\s*[${SEPARATORS}])+`, 'g');
const EDGE_SEPARATORS = new RegExp(`^[\\s${SEPARATORS}]+|[\\s${SEPARATORS}]+$`, 'g');
/** Default coding-link preview title used by the backend resolver. */
export const GENERIC_TITLE = 'Coding Interview';
/** Mirrors Zoom's vendor default topic and is deliberately not localized. */
export const ZOOM_DEFAULT_TOPIC = 'Zoom Meeting';

/** Checks that a non-blank template uses only the provided token vocabulary. */
export const isValidTitleTemplate = (
  template: string,
  tokens: readonly string[] = CODING_LINK_TITLE_TOKENS
): boolean => {
  if (!template.trim()) return false;
  const stripped = template.replace(TOKEN_PATTERN, (match, token) => (tokens.includes(token) ? '' : match));
  return !stripped.includes('{') && !stripped.includes('}');
};
/** Controls omission of identity tokens for coding-tool privacy settings. */
export type PreviewIdentityOptions = { omitName: boolean; omitEmail: boolean };

/** Renders a preview with caller samples and separator cleanup. */
export const renderTitleTemplatePreview = (
  template: string,
  identity: PreviewIdentityOptions,
  tokens: readonly string[] = CODING_LINK_TITLE_TOKENS,
  sampleValues: Record<string, string> = CODING_LINK_PREVIEW_SAMPLE_VALUES,
  previewFallback = 'Coding Interview'
): string => {
  const substituted = template.replace(TOKEN_PATTERN, (match, token) => {
    if (!tokens.includes(token)) return '';
    if (token === 'CANDIDATE_NAME' && identity.omitName) return '';
    if (token === 'CANDIDATE_EMAIL_ADDRESS' && identity.omitEmail) return '';
    return sampleValues[token] ?? '';
  });
  const tidied = substituted
    .replace(EMPTY_BRACKETS, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(DOUBLED_SEPARATORS, '$1')
    .replace(EDGE_SEPARATORS, '')
    .trim();
  return tidied === '' ? previewFallback : tidied;
};
